// frontend/src/pages/storeActivity/timelineDetail.js
//
// The one-line "what was it" on a Recent-activity row.
//
// Most rows carry a ready-made `detail` string from the backend (a PO
// number, a path, a query). The rows this module exists for are the ones
// whose properties are a small STRUCTURE — the two setup answers, the
// approval verdict, a scheduled run, a synchronization, a health alert, a
// reorder-levels save — which have to be read into words with the viewer's
// own formatting.
//
// Driven by the event type, never by which properties happen to be
// present, so a payload that changes shape renders the backend's plain
// `detail` instead of something misleading. An event type with no case here
// — including one since retired from the taxonomy — renders that plain
// `detail` too: this module never guesses what an unknown row meant.
//
// Every string goes through the `t` the caller hands in; nothing here builds
// merchant-facing copy of its own.

import { stateLabel } from './billingStateRow';
import { pipelineLabel } from './text';

export function timelineDetail(row, t) {
  const d = row.properties;
  switch (row.event_type) {
    case 'settings_updated':
      // The two questions the install actually asks the merchant — where
      // categories come from, and whether suppliers are created from product
      // vendors (shopify_app/views/api.py stamps both on the save). Any other
      // settings save has neither and falls through to the plain detail.
      if (d.category_source === undefined || d.supplier_source === undefined) return row.detail;
      return t('storeActivity.install.setupChoice', {
        category: t(`storeActivity.install.categorySource.${d.category_source}`),
        supplier: t(`storeActivity.install.supplierSource.${d.supplier_source}`),
      });

    case 'subscription_approval_requested':
      return t('storeActivity.install.pushed', {
        tier: d.tier,
        interval: t(`storeActivity.install.interval.${d.billing_interval}`),
      });

    case 'subscription_approval_returned':
      // A word, not a boolean — see record_approval_return for why the event
      // carries one. Both values are compared, so a verdict outside the
      // vocabulary renders as its raw code (the backend's ``detail``) rather
      // than silently reading as a decline.
      if (d.verdict === 'approved') return t('storeActivity.install.approved');
      if (d.verdict === 'declined') return t('storeActivity.install.declined');
      return row.detail;

    case 'subscription_changed':
      return t('storeActivity.install.transition', {
        from: stateLabel(d.from_state, t),
        to: stateLabel(d.to_state, t),
      });

    case 'scheduled_job_run': {
      const run = t('storeActivity.automation.jobRow', {
        pipeline: pipelineLabel(d.pipeline, t),
        status: t(`storeActivity.automation.status.${d.status}`),
      });
      // The monthly forecast is a step of the nightly run, not a run of its
      // own; the night it did its work is the one row a reader looks for.
      // Rows written before `steps` existed carry none.
      const monthly = (d.steps ?? []).some(
        (s) => s.step === 'forecast_full' && s.status === 'succeeded',
      );
      return monthly ? `${run} · ${t('storeActivity.automation.monthlyForecastRan')}` : run;
    }

    case 'scheduled_job_requeued':
      return t('storeActivity.automation.requeueRow', {
        pipeline: pipelineLabel(d.pipeline, t),
        requeue: d.requeue,
        cap: d.requeue_cap,
      });

    case 'channel_sync_completed':
      return t('storeActivity.sync.completedRow', {
        type: t(`storeActivity.sync.type.${d.sync_type}`),
        items: d.items_synced,
        orders: d.orders_synced,
      });

    case 'channel_sync_failed':
      return t('storeActivity.sync.failedRow', {
        type: t(`storeActivity.sync.type.${d.sync_type}`),
        reason: t(`storeActivity.sync.reason.${d.reason}`),
        message: d.message,
      });

    // What changed in the store (the backend's own sentence) and what the
    // channel said about the shop; the raw error text stays off the row.
    case 'channel_update_refused':
      return t('storeActivity.refusal.row', {
        what: d.what,
        why: t(`storeActivity.refusal.${d.refusal}`),
      });

    // A grid save (`rows`) or "Set for all" (`all`) — ReorderLevelsViewSet
    // describes both. A refused request, and rows written before the event
    // described itself, carry no `scope`.
    case 'reorder_levels_updated': {
      let items;
      if (d.scope === 'rows') {
        items = t('storeActivity.reorderLevels.saved', { saved: d.items_saved, count: d.items_total });
      } else if (d.scope === 'all') {
        items = t('storeActivity.reorderLevels.setForAll', { count: d.items });
      } else {
        return row.detail;
      }
      const cleared = d.cleared.includes('expected_monthly_sales')
        ? t('storeActivity.reorderLevels.expectedCleared')
        : null;
      return [reorderLevelFields(d.fields, t), cleared, items].filter(Boolean).join(' · ');
    }

    // Old rows may name a category since retired, so the code stands in for
    // a missing sentence rather than a raw key.
    case 'health_anomaly_detected':
      return t(`storeActivity.anomalies.category.${d.category}`, { defaultValue: d.category });

    case 'health_anomaly_resolved':
      return t('storeActivity.anomalies.resolvedRow', {
        finding: t(`storeActivity.anomalies.category.${d.category}`, { defaultValue: d.category }),
        by: t(`storeActivity.anomalies.resolvedBy.${d.resolved_by}`),
      });

    default:
      return row.detail;
  }
}

// Which of the two reorder levels a write gave a value; none when no entry
// of a grid save went through, or when it only cleared expected sales.
function reorderLevelFields(fields, t) {
  const minimum = fields.includes('min_inventory_units');
  const expected = fields.includes('expected_monthly_sales');
  if (minimum && expected) return t('storeActivity.reorderLevels.fields.both');
  if (minimum) return t('storeActivity.reorderLevels.fields.minimum');
  if (expected) return t('storeActivity.reorderLevels.fields.expected');
  return null;
}
